import { Drawer, DrawerContent, Button, toast } from '@pella/sash-framework';
import { useUpdateRequest, useLinkedActionCompletion } from '../../../hooks';
import { STATUSES, TRANSACTION_TYPES } from '../../../data/constants';
import { LinkedActionConfirmModal } from '../LinkedActionConfirmModal/LinkedActionConfirmModal';

import * as classes from './TransactionDrawer.module.css';

/**
 * Converts SCREAMING_SNAKE_CASE to PascalCase
 * @param {string} str - The string to convert
 * @returns {string} The PascalCase version
 */
const toPascalCase = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

/**
 * Component for the Transaction Drawer.
 * @param {Local.TransactionDrawerProps} props - Component props
 */
export const TransactionDrawer = ({ open, close, onComplete, disableSubmit, request, children }) => {
  const { mutate: updateRequest } = useUpdateRequest();
  const { completeWithLinkedActionCheck, isLinkedActionPromptOpen, linkedActionDescription, onDecline, onConfirm } =
    useLinkedActionCompletion();
  const transactionId = toPascalCase(request?.action?.transactionId || '');

  const handleTitle = () => {
    switch (request?.action?.transactionId) {
      case TRANSACTION_TYPES.TRAILER_TO_DOCK:
        return 'Trailer to Dock';
      case TRANSACTION_TYPES.SUB_INVENTORY_TRANSFER:
        return 'Sub Inventory Transfer';
      case TRANSACTION_TYPES.DIRECT_ORG_TRANSFER:
        return 'Direct Organization Transfer';
      case TRANSACTION_TYPES.ISSUE_TO_LINE:
        return 'Issue to Line';
      default:
        return 'Transaction';
    }
  };

  /**
   * Updates the request to Completed status and closes the drawer.
   * @param {string} errorMessage - Toast message shown if the status update fails.
   */
  const handleCompleteRequest = (errorMessage) => {
    if (!request?.id) {
      toast.error('Request ID is missing. Cannot update status.');
      return;
    }

    completeWithLinkedActionCheck(request, () =>
      updateRequest(
        { id: request.id, status: STATUSES.COMPLETED },
        {
          onSuccess: () => {
            toast.success('Request completed successfully!');
            close();
          },
          onError: () => {
            toast.error(errorMessage);
          },
        },
      ),
    );
  };

  const handleBypass = () => {
    handleCompleteRequest('Error bypassing transaction. Status may not be updated. Please refresh.');
  };

  const handleSubmit = () => onComplete(completeWithLinkedActionCheck);

  return (
    <Drawer direction='bottom' open={open} onOpenChange={close}>
      <DrawerContent className={classes.drawer} showClose={false}>
        <div className={classes.content}>
          <b>{handleTitle()}</b>
          {children}
          <div className={classes.drawerFooter}>
            <Button
              data-testid={`bypass${transactionId}`}
              className={classes.button}
              color='tertiary'
              onClick={handleBypass}
            >
              Bypass
            </Button>
            <Button data-testid={`cancel${transactionId}`} className={classes.button} color='secondary' onClick={close}>
              CANCEL
            </Button>
            <Button
              data-testid={`submit${transactionId}`}
              className={classes.button}
              color='primary'
              onClick={handleSubmit}
              disabled={disableSubmit}
            >
              COMPLETE
            </Button>
          </div>
        </div>
      </DrawerContent>
      <LinkedActionConfirmModal
        isOpen={isLinkedActionPromptOpen}
        description={linkedActionDescription}
        onDecline={onDecline}
        onConfirm={onConfirm}
      />
    </Drawer>
  );
};
